import { useState } from "react";
import { format, subDays, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar as CalendarIcon, Users, DollarSign, Receipt, TrendingUp, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { useTicketMedioReport } from "@/hooks/useTicketMedioReport";
import { TicketMedioChart } from "@/components/reports/TicketMedioChart";
import { exportReportToPDF, exportReportToExcel } from "@/lib/report-export";

type DatePreset = "today" | "yesterday" | "last7" | "last30" | "thisMonth" | "lastMonth" | "custom";

export default function TicketMedioPage() {
  const [datePreset, setDatePreset] = useState<DatePreset>("last7");
  const [dateFrom, setDateFrom] = useState<Date>(subDays(new Date(), 6));
  const [dateTo, setDateTo] = useState<Date>(new Date());

  const { data, isLoading } = useTicketMedioReport({
    dateFrom: format(dateFrom, "yyyy-MM-dd"),
    dateTo: format(dateTo, "yyyy-MM-dd"),
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const handlePresetChange = (preset: DatePreset) => {
    setDatePreset(preset);
    const today = new Date();

    switch (preset) {
      case "today":
        setDateFrom(today);
        setDateTo(today);
        break;
      case "yesterday":
        const yesterday = subDays(today, 1);
        setDateFrom(yesterday);
        setDateTo(yesterday);
        break;
      case "last7":
        setDateFrom(subDays(today, 6));
        setDateTo(today);
        break;
      case "last30":
        setDateFrom(subDays(today, 29));
        setDateTo(today);
        break;
      case "thisMonth":
        setDateFrom(startOfMonth(today));
        setDateTo(endOfMonth(today));
        break;
      case "lastMonth":
        const lastMonth = subMonths(today, 1);
        setDateFrom(startOfMonth(lastMonth));
        setDateTo(endOfMonth(lastMonth));
        break;
    }
  };

  const handleExportPDF = () => {
    if (!data) return;
    
    const exportData = data.dailyData.map((item) => ({
      Data: format(new Date(item.date + "T12:00:00"), "dd/MM/yyyy"),
      Pessoas: item.totalPeople,
      Vendas: formatCurrency(item.totalSales),
      Pedidos: item.orderCount,
      "Ticket/Pessoa": formatCurrency(item.ticketPerPerson),
      "Ticket/Pedido": formatCurrency(item.ticketPerOrder),
    }));

    exportReportToPDF(exportData, "Relatório de Ticket Médio", `${format(dateFrom, "dd-MM-yyyy")}_${format(dateTo, "dd-MM-yyyy")}`);
  };

  const handleExportExcel = () => {
    if (!data) return;
    
    const exportData = data.dailyData.map((item) => ({
      Data: format(new Date(item.date + "T12:00:00"), "dd/MM/yyyy"),
      Pessoas: item.totalPeople,
      Vendas: item.totalSales,
      Pedidos: item.orderCount,
      "Ticket por Pessoa": item.ticketPerPerson,
      "Ticket por Pedido": item.ticketPerOrder,
    }));

    exportReportToExcel(exportData, "Ticket Médio", `ticket-medio_${format(dateFrom, "dd-MM-yyyy")}_${format(dateTo, "dd-MM-yyyy")}`);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Ticket Médio por Pessoa</h1>
          <p className="text-muted-foreground">
            Análise do faturamento dividido pelo número de clientes
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            {(
              [
                { key: "today", label: "Hoje" },
                { key: "yesterday", label: "Ontem" },
                { key: "last7", label: "7 dias" },
                { key: "last30", label: "30 dias" },
                { key: "thisMonth", label: "Este mês" },
                { key: "lastMonth", label: "Mês anterior" },
              ] as { key: DatePreset; label: string }[]
            ).map((preset) => (
              <Button
                key={preset.key}
                variant={datePreset === preset.key ? "default" : "ghost"}
                size="sm"
                onClick={() => handlePresetChange(preset.key)}
              >
                {preset.label}
              </Button>
            ))}
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <CalendarIcon className="h-4 w-4" />
                {format(dateFrom, "dd/MM", { locale: ptBR })} -{" "}
                {format(dateTo, "dd/MM", { locale: ptBR })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="range"
                selected={{ from: dateFrom, to: dateTo }}
                onSelect={(range) => {
                  if (range?.from) setDateFrom(range.from);
                  if (range?.to) setDateTo(range.to);
                  setDatePreset("custom");
                }}
                numberOfMonths={2}
                locale={ptBR}
              />
            </PopoverContent>
          </Popover>

          <Button variant="outline" size="sm" onClick={handleExportPDF}>
            <FileDown className="h-4 w-4 mr-2" />
            PDF
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportExcel}>
            <FileDown className="h-4 w-4 mr-2" />
            Excel
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Pessoas</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <div className="text-2xl font-bold">{data?.totalPeople || 0}</div>
            )}
            <p className="text-xs text-muted-foreground">clientes no período</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Faturamento Total</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-32" />
            ) : (
              <div className="text-2xl font-bold">{formatCurrency(data?.totalSales || 0)}</div>
            )}
            <p className="text-xs text-muted-foreground">{data?.totalOrders || 0} pedidos</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ticket por Pessoa</CardTitle>
            <TrendingUp className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-28" />
            ) : (
              <div className="text-2xl font-bold text-primary">
                {formatCurrency(data?.ticketPerPerson || 0)}
              </div>
            )}
            <p className="text-xs text-muted-foreground">média por cliente</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ticket por Pedido</CardTitle>
            <Receipt className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-28" />
            ) : (
              <div className="text-2xl font-bold">{formatCurrency(data?.ticketPerOrder || 0)}</div>
            )}
            <p className="text-xs text-muted-foreground">média por pedido</p>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      {isLoading ? (
        <Skeleton className="h-[350px] w-full" />
      ) : data && data.dailyData.length > 0 ? (
        <TicketMedioChart data={data.dailyData} />
      ) : (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Nenhum dado encontrado para o período selecionado
          </CardContent>
        </Card>
      )}

      {/* Data Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Detalhamento por Dia</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : data && data.dailyData.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead className="text-right">Pessoas</TableHead>
                  <TableHead className="text-right">Vendas</TableHead>
                  <TableHead className="text-right">Pedidos</TableHead>
                  <TableHead className="text-right">Ticket/Pessoa</TableHead>
                  <TableHead className="text-right">Ticket/Pedido</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.dailyData.map((row) => (
                  <TableRow key={row.date}>
                    <TableCell>
                      {format(new Date(row.date + "T12:00:00"), "dd/MM/yyyy", { locale: ptBR })}
                    </TableCell>
                    <TableCell className="text-right">{row.totalPeople}</TableCell>
                    <TableCell className="text-right">{formatCurrency(row.totalSales)}</TableCell>
                    <TableCell className="text-right">{row.orderCount}</TableCell>
                    <TableCell className="text-right font-medium text-primary">
                      {row.totalPeople > 0 ? formatCurrency(row.ticketPerPerson) : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(row.ticketPerOrder)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center text-muted-foreground py-4">
              Nenhum dado encontrado para o período selecionado
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
